import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Sparkles, TrendingUp, Heart, ShieldCheck, Users, ArrowRight, Check, MessageCircle, Send, Lock } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import { trackAmbassadorApply } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';

const API = `${window.location.origin}/api`;

const PLATFORMS = ['TikTok', 'Facebook', 'Instagram', 'YouTube', 'Twitter / X', 'In-person / store', 'Other'];

export default function AmbassadorApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Pull both loginWithGoogle (for the inline signup flow) AND isAuthenticated
  // so the Distributor tier card can flip from "locked" → "unlocked" the moment
  // an ambassador signs in. Same component, reactive to auth state — gives
  // newly-onboarded ambassadors a small "welcome to the family" reveal.
  const { loginWithGoogle, isAuthenticated, user } = useAuth();
  const wholesaleUnlocked = !!isAuthenticated && user && user.role !== 'customer';
  const referrerSlug = searchParams.get('ref') || '';
  const [cfg, setCfg] = useState({
    tester_kit_price: 1899,
    tester_kit_referral_commission: 200,
    ambassador_commission_min: 100,
    ambassador_commission_max: 100,
    monthly_top_seller_cash_bonus: 300,
    tier_thresholds: { bronze: 10, silver: 30, gold: 100 },
  });
  const [form, setForm] = useState({
    name: '', email: '', phone: '', social_handle: '', social_platform: 'TikTok',
    audience_size: '', monthly_sales_estimate: '', why_us: '', where_promote: '', messenger_link: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [referrerName, setReferrerName] = useState('');

  // Pull live editable copy (commissions, prices) from admin config
  useEffect(() => {
    axios.get(`${API}/business-config/public`).then(r => setCfg(c => ({ ...c, ...r.data }))).catch(() => {});
  }, []);

  // Resolve referrer's display name (best-effort, hides errors)
  useEffect(() => {
    if (!referrerSlug) return;
    axios.get(`${API}/r/${referrerSlug}`)
      .then(r => setReferrerName(r.data?.ambassador?.display_name || ''))
      .catch(() => {});
  }, [referrerSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.social_handle.trim() || !form.why_us.trim()) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/ambassador/apply`, { ...form, referrer_slug: referrerSlug || undefined });
      trackAmbassadorApply();

      // Auto-login: store token + user, then send to tester kit checkout
      const data = res.data;
      if (data?.token) {
        loginWithGoogle(data.token, { id: data.id, email: data.email, name: data.name, role: data.role, phone: data.phone });
        toast.success('Account created! Now pay your Tester Kit to activate.');
        // Show temp password briefly so they can save it
        if (data.temp_password) {
          toast.info(`Save your temp password: ${data.temp_password}`, { duration: 12000 });
        }
        navigate('/tester-kit-checkout');
      } else {
        toast.success(data?.message || 'Application received!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <main className="min-h-screen bg-[#0A0A0A]">
      <SEOHead
        title="Become a JS Fragrance Ambassador"
        description="Sell Filipino-made luxury EDP at 30% oil concentration (Extrait-level). Up to 40% commission, wholesale pricing, content kit, dealer community. Apply to become a JS Fragrance ambassador or distributor."
        url="https://jsfragrances.com/ambassador-apply"
      />
      <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Become an Ambassador', path: '/ambassador-apply' }]} />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C7D3F]/15 via-transparent to-[#D4AF37]/10 pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-20 text-center">
          {referrerName && (
            <div className="mb-6 inline-block border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-sans" data-testid="referrer-banner">
              You were referred by <span className="text-white font-medium normal-case">{referrerName}</span>
            </div>
          )}
          <Crown className="w-12 h-12 text-[#D4AF37] mx-auto mb-5" strokeWidth={1.4} />
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4 font-sans">Ambassador Program</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-[1.05] mb-5" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Build Your Income.<br /><span className="text-[#D4AF37]">Carry Our Story.</span>
          </h1>
          <p className="text-white/60 text-base sm:text-lg font-light max-w-2xl mx-auto leading-relaxed mb-2">
            We don&apos;t sell perfume. We sell <em>memories</em> — bottled.
          </p>
          <p className="text-white/40 text-sm font-sans max-w-2xl mx-auto leading-relaxed">
            Become a JS Fragrance ambassador and turn your audience into recurring income. Every TikTok review, every story post, every recommendation earns you commission — for as long as your buyer stays a customer.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="#apply" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-7 py-3.5 text-xs uppercase tracking-[0.22em] font-sans font-bold hover:bg-[#F3E5AB] transition-colors">
              Apply now <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#how-it-works" className="text-white/60 text-xs uppercase tracking-[0.22em] font-sans hover:text-[#D4AF37]">How it works ↓</a>
          </div>
        </div>
      </section>

      {/* WHY US — STORY ANGLE */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-12">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 font-sans">Why JS Fragrance</p>
          <h2 className="text-3xl sm:text-4xl text-white font-light leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            We&apos;re not just another reseller brand.
          </h2>
        </div>
        <div className="space-y-6 text-white/65 text-base sm:text-lg leading-relaxed font-light">
          <p>
            Most fragrance &quot;brands&quot; in the Philippines are repackaged dupes. Same oils, different stickers, hollow story.
          </p>
          <p>
            <strong className="text-white">JS Fragrance is different.</strong> Every bottle is hand-blended in our Manila perfumery — no overseas factory, no middleman, no counterfeit risk in your supply chain. Our JS Originals are exclusive scents that exist nowhere else. Every fragrance is EDP bottled at <em>30% oil concentration</em> — Extrait-level strength, the strongest tier normally reserved for niche luxury houses.
          </p>
          <p>
            We&apos;re building a brand that will be searched by name in 10 years. And we&apos;re inviting the right people to come build with us.
          </p>
          <p className="text-[#D4AF37] italic">
            You&apos;re not selling perfume. You&apos;re selling a story, a feeling, an identity.
          </p>
        </div>
      </section>

      {/* PERKS GRID */}
      <section className="bg-[#0F0F0F] border-y border-white/5 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 font-sans">What you get</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Built to make you successful.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(() => {
              const cmin = Number(cfg.ambassador_commission_min) || 0;
              const cmax = Number(cfg.ambassador_commission_max) || 0;
              const earnLabel = (cmin && cmax && cmin !== cmax)
                ? `Earn ₱${cmin}-₱${cmax} per bottle`
                : `Earn ₱${cmax || cmin} per bottle`;
              return [
              { icon: TrendingUp, title: earnLabel, desc: `Profit margin on every bottle YOU sell. Plus a one-time ₱${cfg.tester_kit_referral_commission} bonus when your direct referee gets approved.` },
              { icon: Sparkles, title: 'Your unique referral link', desc: 'Earn commission on every customer sale tracked back to you — even when buyers come back later.' },
              { icon: ShieldCheck, title: 'Hand-blended in PH', desc: 'Sell a Filipino brand made in Manila. No middlemen, no counterfeits, no embarrassing returns.' },
              { icon: Heart, title: 'Brand support kit', desc: 'Product photos, captions, scent stories, and TikTok hooks ready for you to post.' },
              { icon: Users, title: 'Dealer community', desc: 'Private Messenger group with other ambassadors trading tips, drops, and strategy.' },
              { icon: Crown, title: 'Monthly Top Seller rewards', desc: `Cash bonus (₱${cfg.monthly_top_seller_cash_bonus}) + free bottle + spotlight feature + official certificate.` },
            ];
            })().map((perk) => (
              <div key={perk.title} className="bg-[#141414] border border-white/5 p-6 hover:border-[#D4AF37]/30 transition-colors">
                <perk.icon className="w-6 h-6 text-[#D4AF37] mb-4" strokeWidth={1.5} />
                <h3 className="text-white text-base font-medium font-sans mb-2">{perk.title}</h3>
                <p className="text-white/55 text-sm font-light leading-relaxed">{perk.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GROWTH PATH — 1 Ambassador role + 3 volume-based Distributor tiers */}
      <section id="how-it-works" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-12">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 font-sans">Your growth path</p>
          <h2 className="text-3xl sm:text-4xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Start as Ambassador. <span className="italic text-[#D4AF37]">Scale into Distributor.</span>
          </h2>
          <p className="text-white/50 text-sm font-sans mt-3 max-w-2xl mx-auto">
            One role, one entry. As your orders grow, you unlock <strong className="text-white">better wholesale pricing</strong> — same product, lower cost per bottle, larger profit per sale. No quotas, no downlines, no pay-to-play.
          </p>
        </div>

        {/* AMBASSADOR — the entry. Single tier, flat margin. Clear, no confusion. */}
        <div className="bg-[#141414] border border-[#D4AF37]/40 p-6 sm:p-8 mb-8"
             style={{ boxShadow: '0 0 50px -12px rgba(212, 175, 55, 0.2)' }}
             data-testid="path-ambassador-card">
          <div className="grid md:grid-cols-[1fr_1fr] gap-6 items-center">
            <div>
              <span className="inline-block bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 mb-4">
                You Start Here
              </span>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans mb-1">Ambassador</p>
              <h3 className="text-5xl text-white font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                ₱100 <span className="text-2xl text-white/40">/ bottle</span>
              </h3>
              <p className="text-white/50 text-sm font-sans">
                Flat profit per 50ml bottle sold via your referral link. <strong className="text-white">No volume requirement</strong> — earn from day 1.
              </p>
              <p className="text-white/30 text-xs font-sans mt-3">
                SRP ₱355 · Your cost ₱255 · You keep ₱100 (28% margin)
              </p>
            </div>
            <ul className="space-y-2.5 text-sm font-sans text-white/75">
              <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Personal referral link + 30-day cookie</span></li>
              <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Full content pack (photos, captions, reel hooks)</span></li>
              <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span><strong className="text-white">₱200 sign-up bonus</strong> per Tester Kit referral</span></li>
              <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Private ambassador Messenger community</span></li>
              <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Real-time dashboard, wallet &amp; GCash withdrawal</span></li>
              <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Monthly Top-Seller certificate when you rank</span></li>
            </ul>
          </div>
          <p className="text-white/40 text-xs font-sans mt-6 pt-5 border-t border-white/5">
            No fee. No quota. Apply once — your profile and link are forever yours.
          </p>
        </div>

        {/* DISTRIBUTOR — by invitation, volume-based pricing tiers */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-3 text-[#D4AF37]/60">
            <span className="h-px w-12 bg-[#D4AF37]/30" />
            <p className="text-[10px] uppercase tracking-[0.22em] font-sans">When you grow</p>
            <ArrowRight className="w-4 h-4 rotate-90" />
          </div>
        </div>

        {/* DISTRIBUTOR — by invitation, NO public prices. We deliberately keep
            the actual wholesale numbers behind a verified-partner gate (handled
            on /wholesale) so we don't undercut existing distributors or invite
            retail shoppers to chase quantity breaks. The card here only sells
            the *shape* of the opportunity (3 volume bands + perks) — exact
            pesos are revealed after sign-in / approval. */}
        <div className="bg-gradient-to-br from-[#141414] via-[#1a1408] to-[#141414] border border-white/10 p-6 sm:p-8" data-testid="path-distributor-card">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div>
              <span className="inline-block border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.22em] font-bold px-2.5 py-1 mb-3">
                {wholesaleUnlocked ? 'Unlocked for You' : 'By Invitation'}
              </span>
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/60 font-sans mb-1">Distributor</p>
              <h3 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {wholesaleUnlocked
                  ? <>Bulk wholesale — <span className="italic text-[#D4AF37]">your live rates</span></>
                  : 'Bulk wholesale — confidential pricing'}
              </h3>
            </div>
            <p className="text-white/40 text-xs font-sans max-w-xs">
              {wholesaleUnlocked
                ? <>Welcome, <strong className="text-white">{user?.name?.split(' ')[0] || 'partner'}</strong>. Your verified rates below — same price you'll see in /wholesale.</>
                : <>We invite ambassadors who consistently move volume. <strong className="text-white">No application</strong> — earned, not asked.</>}
            </p>
          </div>

          {/* Three volume tiers — locked or unlocked depending on auth state.
              Unlocked state shows real pesos pulled from the same PDF source.
              Locked state shows tease copy + a sign-in CTA. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="distributor-volume-tiers">
            {[
              { label: 'Distributor', range: '100–299 bottles', cost: 246, profit: 109, pct: 31, tease: 'Distributor rate unlocked' },
              { label: 'Bulk Partner', range: '300–499 bottles', cost: 238, profit: 117, pct: 33, tease: 'Lower cost, larger profit', highlight: true },
              { label: 'Super Bulk', range: '500+ bottles', cost: 232, profit: 123, pct: 35, tease: 'Our best wholesale rate' },
            ].map(t => (
              <div
                key={t.label}
                className={`p-5 border ${t.highlight ? 'bg-[#D4AF37]/[0.05] border-[#D4AF37]/30' : 'bg-[#0F0F0F] border-white/10'}`}
                data-testid={`dist-tier-${t.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-sans mb-1">{t.label}</p>
                <p className="text-white/50 text-xs font-sans mb-4">{t.range}</p>

                {wholesaleUnlocked ? (
                  <>
                    <p className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      ₱{t.profit}<span className="text-base text-white/40"> profit</span>
                    </p>
                    <p className="text-white/40 text-xs font-sans mt-1">per bottle · {t.pct}% margin</p>
                    <p className="text-white/30 text-[11px] font-sans mt-3 pt-3 border-t border-white/5">
                      Your cost: ₱{t.cost} · SRP: ₱355
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <Lock className="w-3.5 h-3.5 text-[#D4AF37]/60" />
                      <span className="text-white/30 text-xs font-sans italic">Confidential rate</span>
                    </div>
                    <p className="text-white/60 text-xs font-sans">{t.tease}</p>
                  </>
                )}
              </div>
            ))}
          </div>

          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm font-sans text-white/75 mt-6 pt-6 border-t border-white/5">
            <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Reserved inventory + priority restocks</span></li>
            <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Direct dispatch (Lalamove / J&amp;T pickup)</span></li>
            <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>1-on-1 onboarding with the JS founder</span></li>
            <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Co-marketing on our official IG / FB</span></li>
            <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Private-label / co-branding option</span></li>
            <li className="flex items-start gap-2"><span className="text-[#D4AF37] flex-shrink-0">✓</span><span>Reseller kiosk starter pack available</span></li>
          </ul>

          <div className="mt-6 pt-5 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            {wholesaleUnlocked ? (
              <>
                <p className="text-white/50 text-xs font-sans max-w-md">
                  ✓ Verified-partner rates. These are the same rates that appear in <Link to="/wholesale" className="text-[#D4AF37] underline">/wholesale</Link>. Keep them private.
                </p>
                <Link
                  to="/wholesale"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.18em] font-medium font-sans hover:bg-[#E5C158] transition-colors whitespace-nowrap"
                  data-testid="distributor-wholesale-cta"
                >
                  Go to Wholesale Hub <ArrowRight className="w-3 h-3" />
                </Link>
              </>
            ) : (
              <>
                <p className="text-white/40 text-xs font-sans max-w-md">
                  Wholesale rates are released to verified partners only, after onboarding. This protects every dealer's margin.
                </p>
                <Link
                  to="/wholesale"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] text-xs uppercase tracking-[0.18em] font-sans hover:bg-[#D4AF37]/10 transition-colors whitespace-nowrap"
                  data-testid="distributor-unlock-cta"
                >
                  <Lock className="w-3 h-3" /> Sign in to unlock
                </Link>
              </>
            )}
          </div>
        </div>

        <p className="text-center text-white/30 text-xs font-sans mt-8 max-w-2xl mx-auto">
          That's it — one role with room to scale. <strong className="text-white/60">No downlines. No recruiting quotas. No pay-to-play.</strong> Your earnings come from your <em>own</em> sales and the people you bring directly. Period.
        </p>
      </section>

      {/* HOW-IT-WORKS STEPS */}
      <section className="bg-[#0F0F0F] border-y border-white/5 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>How it works</h2>
          </div>
          <ol className="space-y-6 text-white/70 text-base font-sans">
            {[
              { t: 'Apply', d: 'Fill out the form below. We respond within 1-2 business days via email or Facebook Messenger.' },
              { t: 'Get your kit', d: 'Once approved, you receive your unique referral link, wholesale catalog, content pack, and welcome to our private dealer community.' },
              { t: 'Promote your way', d: 'TikTok, Instagram Reels, FB groups, in-person, your own salon — we don\'t care HOW, we care that it\'s authentic to you.' },
              { t: 'Earn forever', d: 'Every customer who uses your link earns you commission on their purchase + every repeat order they make. Cookies last 30 days, accounts last forever.' },
            ].map((step, idx) => (
              <li key={step.t} className="flex gap-4">
                <span className="flex-shrink-0 w-9 h-9 bg-[#D4AF37] text-black flex items-center justify-center font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{idx + 1}</span>
                <div>
                  <h3 className="text-white text-lg font-medium mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{step.t}</h3>
                  <p className="text-white/55 text-sm font-light leading-relaxed">{step.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="max-w-2xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 font-sans">Apply now</p>
          <h2 className="text-3xl sm:text-4xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Tell us about you.</h2>
          <p className="text-white/40 text-sm font-sans mt-3">We read every application personally. No bots. No copy-paste rejections.</p>
        </div>

        <form onSubmit={submit} className="bg-[#141414] border border-white/5 p-6 sm:p-8 space-y-5" data-testid="ambassador-apply-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Full Name *</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20" placeholder="Juan Dela Cruz" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Email *</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20" placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Phone *</label>
              <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required
                className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20" placeholder="09XX XXX XXXX" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Main Platform *</label>
              <select value={form.social_platform} onChange={e => setForm({ ...form, social_platform: e.target.value })}
                className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none">
                {PLATFORMS.map(p => <option key={p} value={p} className="bg-[#0A0A0A]">{p}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Social Handle / Page Name *</label>
            <input type="text" value={form.social_handle} onChange={e => setForm({ ...form, social_handle: e.target.value })} required
              className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20" placeholder="@yourname or facebook.com/yourpage" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Audience size <span className="text-white/30">(optional)</span></label>
              <input type="text" value={form.audience_size} onChange={e => setForm({ ...form, audience_size: e.target.value })}
                className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20" placeholder="e.g. 5K followers" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Monthly bottle estimate <span className="text-white/30">(optional)</span></label>
              <input type="text" value={form.monthly_sales_estimate} onChange={e => setForm({ ...form, monthly_sales_estimate: e.target.value })}
                className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20" placeholder="e.g. 10-20" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Where will you promote? *</label>
            <textarea value={form.where_promote} onChange={e => setForm({ ...form, where_promote: e.target.value })}
              rows={2}
              className="w-full bg-transparent border border-white/15 text-white text-sm p-3 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20 resize-none"
              placeholder="TikTok reviews, FB store, beauty salon, in-person events, etc." />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Why us? *</label>
            <textarea value={form.why_us} onChange={e => setForm({ ...form, why_us: e.target.value })} required maxLength={2000} rows={4}
              className="w-full bg-transparent border border-white/15 text-white text-sm p-3 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20 resize-y"
              placeholder="Why do you want to represent JS Fragrance? What's your story?" />
            <p className="text-white/30 text-[10px] font-sans mt-1">{form.why_us.length} / 2000</p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-sans block mb-1.5">Messenger / FB profile <span className="text-white/30">(optional — for faster contact)</span></label>
            <input type="url" value={form.messenger_link} onChange={e => setForm({ ...form, messenger_link: e.target.value })}
              className="w-full bg-transparent border-b border-white/15 text-white text-sm py-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/20"
              placeholder="https://m.me/yourusername or fb.com/yourprofile" />
            <p className="text-white/30 text-[10px] font-sans mt-1.5"><MessageCircle className="w-3 h-3 inline mr-1" />We message qualified applicants directly on Messenger to onboard faster.</p>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-6 py-4 text-xs uppercase tracking-[0.22em] font-sans font-bold hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
            data-testid="ambassador-submit-btn">
            <Send className="w-3.5 h-3.5" />
            {submitting ? 'Sending…' : 'Submit Application'}
          </button>

          <p className="text-white/30 text-[10px] font-sans text-center">
            By applying you agree to our <Link to="/terms" className="text-[#D4AF37]">Terms of Service</Link> and <Link to="/privacy" className="text-[#D4AF37]">Privacy Policy</Link>.
          </p>
        </form>
      </section>
    </main>
  );
}
